/* Change password (any signed-in role). Keeps the session alive with the new token; other sessions are signed out. */
import { useId, useState, type FormEvent } from "react";
import { useSession } from "../../app/session";
import { useChangePassword } from "../../data/hooks";
import { ApiError } from "../../data/provider";
import { useI18n } from "../../i18n";
import { Button, Card, Field, Input, errMsg, useToast } from "../../ui";
import { fieldMsg } from "./messages";
import PasswordField from "./PasswordField";
import { passwordProblem } from "./passwordPolicy";

export default function ChangePasswordCard() {
  const { L } = useI18n();
  const toast = useToast();
  const { adopt } = useSession();
  const m = useChangePassword();
  const curId = useId();
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [fe, setFe] = useState<Record<string, string | undefined>>({});

  const submit = async (ev: FormEvent) => {
    ev.preventDefault();
    const e: Record<string, string | undefined> = {};
    if (!cur) e.current = L("Enter your current password", "أدخل كلمة المرور الحالية");
    const pp = passwordProblem(next);
    if (pp) e.new = fieldMsg(L, pp);
    else if (next === cur) e.new = fieldMsg(L, "must differ from the current password");
    if (!e.new && confirm !== next) e.confirm = L("The passwords do not match", "كلمتا المرور غير متطابقتين");
    setFe(e);
    if (Object.values(e).some(Boolean)) return;
    try {
      const r = await m.mutateAsync({ current: cur, next });
      adopt(r, { keepCache: true });
      setCur(""); setNext(""); setConfirm("");
      toast(L("Password changed · other devices were signed out", "تم تغيير كلمة المرور · تم تسجيل الخروج من الأجهزة الأخرى"));
    } catch (err) {
      if (err instanceof ApiError && err.fields) setFe({ current: fieldMsg(L, err.fields.current), new: fieldMsg(L, err.fields.new) });
      else toast(errMsg(err), "bad");
    }
  };

  return (
    <Card title={L("Change password", "تغيير كلمة المرور")} sub={L("You'll stay signed in here; every other device is signed out.", "ستبقى مسجلًا هنا، وسيتم تسجيل الخروج من الأجهزة الأخرى.")} testId="change-password">
      <form className="stack" onSubmit={submit} noValidate>
        <Field label={L("Current password", "كلمة المرور الحالية")} htmlFor={curId} err={fe.current}>
          <Input id={curId} type="password" dir="ltr" autoComplete="current-password" value={cur} bad={!!fe.current} onChange={(e) => setCur(e.target.value)} data-testid="cp-current" />
        </Field>
        <PasswordField label={L("New password", "كلمة المرور الجديدة")} value={next} onChange={setNext} err={fe.new} testId="cp-new" />
        <PasswordField label={L("Confirm new password", "تأكيد كلمة المرور الجديدة")} value={confirm} onChange={setConfirm} err={fe.confirm} testId="cp-confirm" meter={false} />
        <div className="row"><Button type="submit" variant="pri" icon="check" loading={m.isPending} data-testid="cp-save">{L("Change password", "تغيير كلمة المرور")}</Button></div>
      </form>
    </Card>
  );
}
